import { Column, Table } from '@tanstack/react-table';

import type { XlsxColumn } from 'src/lib/xlsx/types';
import { z } from 'src/lib/zod';

// 表格查询通用参数的基础 schema
const baseTableSchema = z.object({
  page: z.coerce.number().min(0).optional(),
  size: z.coerce.number().min(10).max(100).optional(),
  sort: z.string().optional(),
});

export type BaseTableQuery = z.infer<typeof baseTableSchema>;

// 辅助类型：将联合类型转换为交叉类型
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void
  ? I
  : never;

/**
 * 解析和校验搜索参数
 *
 * @param search - 原始搜索参数对象
 * @param filterSchemas - 一个或多个过滤器 schema（不包含 page/size/sort）
 * @returns 解析后的数据，包含 baseTableSchema 和所有 filterSchemas 的字段
 */
export function parseSearch<T extends z.ZodObject<any>[]>(
  search: Record<string, any>,
  ...filterSchemas: T
): z.infer<typeof baseTableSchema> & UnionToIntersection<z.infer<T[number]>> {
  // 从 baseTableSchema 开始，依次合并所有 filterSchemas
  let combinedSchema: z.ZodObject<any> = baseTableSchema;
  for (const schema of filterSchemas) {
    combinedSchema = combinedSchema.merge(schema);
  }

  // 分离出值为 null 的字段
  const nullFields: Record<string, null> = {};
  const nonNullSearch: Record<string, any> = {};

  for (const [key, value] of Object.entries(search)) {
    if (value === null) {
      nullFields[key] = null;
    } else {
      nonNullSearch[key] = value;
    }
  }

  // 解析非 null 的字段
  const parsed = combinedSchema.parse(nonNullSearch);

  // 合并 null 字段回去
  return { ...parsed, ...nullFields } as z.infer<typeof baseTableSchema> &
    UnionToIntersection<z.infer<T[number]>>;
}

export const getColumnDisplayName = <TData>(column: Column<TData>) => {
  if (column.columnDef.meta?.displayName) return column.columnDef.meta.displayName;

  const header = column.columnDef.header;
  if (typeof header === 'string') {
    return header;
  }

  return column.id;
};

export interface GetTableHeaderOptions {
  /** 是否为导入生成列（导入时不过滤隐藏列，但会过滤 unimportable 列） */
  forImport?: boolean;
}

/**
 * 从 TanStack Table 获取表头配置
 *
 * @param table - TanStack Table 实例
 * @param options - 配置选项
 * @returns 表头配置（二维数组，支持多层表头）
 *
 * @example
 * ```typescript
 * // 导出时：过滤隐藏列
 * const header = getTableHeader(table);
 *
 * // 导入时：不过滤隐藏列，但过滤 unimportable 列
 * const header = getTableHeader(table, { forImport: true });
 * ```
 */
export function getTableHeader(
  table: Table<any>,
  options: GetTableHeaderOptions = {}
): XlsxColumn[][] {
  const { forImport = false } = options;

  // 导入时：使用 getAllLeafColumns 获取所有列（包括隐藏的列）
  if (forImport) {
    const columns = table
      .getAllLeafColumns()
      .filter((column) => {
        const columnDef = column.columnDef;

        // 过滤掉标记为 unimportable 的列
        if (columnDef.meta?.unimportable) {
          return false;
        }

        // 排除没有数据访问器的 display 列（如 select、actions）
        const hasAccessor = 'accessorKey' in columnDef || 'accessorFn' in columnDef;
        if (!hasAccessor) return false;

        return true;
      })
      .map((column) => {
        const columnDef = column.columnDef;

        // 获取原始的 accessor key
        let key: string | undefined = column.id;

        if ('accessorKey' in columnDef && columnDef.accessorKey) {
          key = columnDef.accessorKey as string;
        } else if ('accessorFn' in columnDef) {
          // 如果是 accessorFn，尝试从 column id 中恢复原始 key
          // TanStack Table 会将 'body.at' 转换为 'body_at'，我们需要反转这个过程
          // 同时需要正确处理数组索引: 'body_motor_motors_0_status' -> 'body.motor.motors[0].status'
          key = column.id
            .replace(/_(\d+)_/g, '[$1].') // 中间的 _数字_ → [数字].
            .replace(/_(\d+)$/g, '[$1]') // 末尾的 _数字 → [数字]
            .replace(/_/g, '.'); // 其他的 _ → .
        }

        return {
          title: getColumnDisplayName(column),
          key,
          colSpan: 1,
          depth: 0,
          isPlaceholder: false,
          width: columnDef.meta?.width as number | undefined,
        };
      });

    return [columns]; // 返回扁平的单层数组
  }

  // 导出时：使用 getHeaderGroups 获取可见列（支持多层表头）
  return table.getHeaderGroups().map((headerGroup) =>
    headerGroup.headers
      .filter((header) => {
        const columnDef = header.column.columnDef;

        // 导出时：过滤隐藏列
        if (!header.column.getIsVisible()) {
          return false;
        }

        // 占位符列需要保留（用于复合表头对齐）
        if (header.isPlaceholder) return true;

        // 分组列（group columns）需要保留
        if ('columns' in columnDef && columnDef.columns) return true;

        // 过滤掉标记为 unexportable 的列
        if (columnDef.meta?.unexportable) {
          return false;
        }

        // 排除没有数据访问器的 display 列（如 select、actions）
        const hasAccessor = 'accessorKey' in columnDef || 'accessorFn' in columnDef;
        if (!hasAccessor) return false;

        return true;
      })
      .map((header) => {
        const columnDef = header.column.columnDef;

        // 获取原始的 accessor key
        let key: string | undefined = header.column.id;

        // 处理分组列（group columns）
        if ('columns' in columnDef && columnDef.columns) {
          // 分组列不需要 key，因为它们不对应数据字段
          key = undefined;
        } else if (!header.isPlaceholder) {
          // 处理普通数据列
          if ('accessorKey' in columnDef && columnDef.accessorKey) {
            key = columnDef.accessorKey as string;
          } else if ('accessorFn' in columnDef) {
            // 如果是 accessorFn，尝试从 column id 中恢复原始 key
            // TanStack Table 会将 'body.at' 转换为 'body_at'，我们需要反转这个过程
            // 同时需要正确处理数组索引: 'body_motor_motors_0_status' -> 'body.motor.motors[0].status'
            key = header.column.id
              .replace(/_(\d+)_/g, '[$1].') // 中间的 _数字_ → [数字].
              .replace(/_(\d+)$/g, '[$1]') // 末尾的 _数字 → [数字]
              .replace(/_/g, '.'); // 其他的 _ → .
          }
        }

        return {
          title: getColumnDisplayName(header.column),
          key: header.isPlaceholder ? undefined : key,
          colSpan: header.colSpan,
          depth: header.depth,
          isPlaceholder: header.isPlaceholder,
          width: columnDef.meta?.width as number | undefined,
        };
      })
  );
}

/**
 * 从 table 的 columnFilters 中获取指定 id 的 filter 值
 */
export function getTableFilter<T = any>(table: Table<any>, id: string): T | undefined {
  const columnFilters = table.getState().columnFilters;
  return columnFilters.find((f) => f.id === id)?.value as T | undefined;
}

/**
 * 批量设置 table 的 columnFilters
 * 传入 null 或 undefined 会移除对应的 filter
 */
export function setTableFilters(table: Table<any>, filters: Record<string, any>) {
  const columnFilters = table.getState().columnFilters;
  const idsToUpdate = Object.keys(filters);
  const originFilters = columnFilters.filter((f) => !idsToUpdate.includes(f.id));
  const filtersToAdd = Object.entries(filters).map(([id, value]) => ({ id, value }));

  table.setColumnFilters([...originFilters, ...filtersToAdd]);
}
